import { getBuildInfo } from "@/lib/buildInfo";

/**
 * Service-worker registration + update detection (main-thread side). Registers
 * `/sw.js`, checks for updates every 30 minutes, and dispatches
 * {@link SW_UPDATE_EVENT} when a new version is waiting — the app shows a toast
 * and only updates when the user accepts. Never updates mid-session on its own.
 */

export const SW_UPDATE_EVENT = "catalogkit:sw-update";
const UPDATE_INTERVAL_MS = 30 * 60 * 1000;

export interface SwUpdateDetail {
  /** The installed-but-waiting worker to activate on user confirmation. */
  worker: ServiceWorker;
}

function announceUpdate(worker: ServiceWorker): void {
  window.dispatchEvent(new CustomEvent<SwUpdateDetail>(SW_UPDATE_EVENT, { detail: { worker } }));
}

/** Ask a waiting worker to activate, then reload once it takes control. */
export function applyUpdate(worker: ServiceWorker): void {
  worker.postMessage({ type: "SKIP_WAITING" });
}

/**
 * Register the service worker. No-op when unsupported. Cache-busts the SW URL
 * with the build hash so a new deploy is fetched. Only meaningful in production
 * (the built `/sw.js` doesn't exist under the dev server).
 */
export function registerServiceWorker(): void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

  window.addEventListener("load", () => {
    const version = getBuildInfo().commitShort;
    void navigator.serviceWorker
      .register(`/sw.js?v=${version}`)
      .then((registration) => {
        // If one is already waiting (updated in a previous tab), surface it.
        if (registration.waiting && navigator.serviceWorker.controller) {
          announceUpdate(registration.waiting);
        }

        registration.addEventListener("updatefound", () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            // "installed" while a controller exists = an update (not first install).
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              announceUpdate(installing);
            }
          });
        });

        // Periodic update check (never auto-applies).
        setInterval(() => void registration.update(), UPDATE_INTERVAL_MS);
      })
      .catch(() => {
        /* registration failures are non-fatal; the app works online regardless */
      });

    // Reload once the new worker takes control (after the user accepts).
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  });
}
